import { DatePicker } from "@mantine/dates";
import { TextInput, Chip, Select, Checkbox, NumberInput } from "@mantine/core";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../components/pageList";
import { IconSettings, IconPhone } from "@tabler/icons";
import FormConfirmation from "../components/formConfirmation";
import RemoteSelect2 from "../components/remoteSelect2";
import ConsultantList from "../components/consultantList";
import Chips from "../components/chips";
import {
  DATA_COUNTRY,
  DATA_EDUCATION_LEVEL,
  DATA_LANGUAGE,
  DATA_NATIONALITY,
  DATA_SALUATATION,
  DATA_ID_TYPE,
  DATA_CLIENT_TYPES,
  DATA_CLIENT_STATUS,
  DATA_CLIENT_DISPOSAL_STATUS,
  DATA_GENDERS,
  DATA_MARIAGE_STATUS,
} from "../data/options";
import AddressList from "../components/addressList";
import PhoneNumberList from "../components/phoneNumberList";
import EmailList from "../components/emailList";
import ContactList from "../components/contactList";
import ClientIdentityList from "../components/clientIdentityList";
import ClientPiDatePicker from "../components/clientPiDatePicker";
import {
  buildClientUpdateValidate,
  CLIENT_CONFIRM_FIELD_LABELS,
} from "./clientFormValidate";
import { applyDefaultIdIssueCountry } from "./clientIdIssueCountry";
import {
  isIdExpiryRequired,
  isMainIdRequired,
  isOtherIdsSectionVisible,
  getClientIdPlaceholder,
} from "./clientIdentity";
import NoIdProvidedCheckbox from "../components/noIdProvidedCheckbox";

export const getClientUpdateFormSchema = (
  t,
  { canChangeClientStatus = false, client = {} } = {},
) => {
  const clientLabel = [client.code, client.name].filter(Boolean).join(" — ");

  const schema = {
    title: clientLabel ? `Update Client — ${clientLabel}` : "Update Client",
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px",
    },
    closeAfterSave: true,
    showSaveBar: false,
    apiEntity: "client",
    initialValues: {
      idType: "HKID",
      idIssueCountry: "Hong Kong",
      otherIds: [],
      extraFieldValues: {},
    },
    validate: buildClientUpdateValidate(t),
    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Type",
            orientation: "vertical",
            style: { minHeight: "400px" },
          },
          steps: [
            { key: "t1-1", label: "Type", icon: IconSettings },

            { key: "t1-2", label: "Client ID", icon: IconSettings },
            { key: "t1-3", label: "Basic Info", icon: IconSettings },
            {
              key: "t1-pi",
              label: "Professional Investor",
              icon: IconSettings,
            },
            { key: "t1-6", label: "Contact Info", icon: IconPhone },
            { key: "t1-4", label: "Consultant", icon: IconSettings },

            { key: "t1-5", label: "Confirm", icon: IconSettings },
          ],
        },
        {
          key: "section-type",
          parent: "t1-1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client Types",
            description: "Please choose type for client",
            withBorder: false,
            ml: 10,
            mt: 0,
          },
        },

        {
          key: "section-ID",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Main ID",
            titleOrder: 5,
            description: "Please enter the primary identity document",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-other-ids",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: isOtherIdsSectionVisible,
          props: {
            title: "Other IDs",
            titleOrder: 5,
            description: "Add additional identity documents for this client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-basicInfo",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.type != "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-basicInfo-corporate",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.type == "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "basicInfo-g",
          parent: "section-basicInfo",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "basicInfo-g1",
              props: {
                span: 6,
              },
            },
            {
              key: "basicInfo-g2",
              props: {
                span: 6,
              },
            },
          ],
        },
        {
          key: "basicInfo-g1-top",
          parent: "basicInfo-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
        },
        {
          key: "name-fields-g",
          parent: "basicInfo-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "name-fields-surname",
              props: {
                span: 4,
              },
            },
            {
              key: "name-fields-given",
              props: {
                span: 8,
              },
            },
          ],
        },
        {
          key: "basicInfo-g1-bottom",
          parent: "basicInfo-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
        },

        {
          key: "section-pi",
          parent: "t1-pi",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Professional Investor",
            titleOrder: 5,
            description:
              "Record professional investor status, related dates, and knowledge of derivatives",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-consultant",
          parent: "t1-4",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Consultant",
            titleOrder: 5,
            description: "Please assign the corresponding consultant",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-confirm",
          parent: "t1-5",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Confirm",
            titleOrder: 3,
            description: clientLabel
              ? `You are updating existing client ${clientLabel}. Please confirm to save changes or go back to revise.`
              : "You are updating an existing client. Please confirm to save changes or go back to revise.",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "tab-contact-wizard",
          parent: "t1-6",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            size: "sm",
            defaultValue: "Phone Numbers",
          },
          tab: [
            {
              key: "tab-wizard-phones",
              label: "Phone Numbers",
            },
            {
              key: "tab-wizard-emails",
              label: "Emails",
            },
            {
              key: "tab-wizard-addresses",
              label: "Address",
            },
            {
              key: "tab-wizard-related-persons",
              label: "Related Person Info",
            },
          ],
        },
      ],

      fields: [
        {
          name: "type",
          component: Chips,
          parent: "section-type",
          props: {
            required: true,
            labelOrder: 5,
            description: "Please Select the type of client.",
            items: DATA_CLIENT_TYPES,
            defaultValue: "INDIVIDUAL",
            multiple: false,
          },
        },
        {
          name: "noIdProvided",
          component: NoIdProvidedCheckbox,
          parent: "section-ID",
          props: {
            label: "No ID provided",
            type: "checkbox",
          },
        },
        {
          name: "idType",
          component: Select,
          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "ID Type",
            placeholder: "",
            data: DATA_ID_TYPE,
            defaultValue: "HKID",
            searchable: true,
            onDateChange: (value, form) =>
              applyDefaultIdIssueCountry(value, form),
          },
        },
        {
          name: "id",
          component: TextInput,
          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "ID No.",
            placeholder: (formValues) =>
              getClientIdPlaceholder(formValues.idType),
          },
        },

        {
          name: "idIssueCountry",
          component: Select,

          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "Issue Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
          },
        },
        {
          name: "idDocExpiryDate",
          component: DatePicker,
          type: "datePicker",
          parent: "section-ID",
          visibleOnly: (formValues) =>
            isMainIdRequired(formValues.idType) && formValues.idType !== "HKID",
          props: {
            required: (formValues) => isIdExpiryRequired(formValues.idType),
            label: "Expiry",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "otherIds",
          component: ClientIdentityList,
          parent: "section-other-ids",
          props: {
            hideTitle: true,
          },
        },
        {
          name: "status",
          component: Select,
          parent: "basicInfo-g1-top",
          props: {
            label: "Status",
            data: DATA_CLIENT_STATUS,
            required: true,
            readOnly: !canChangeClientStatus,
            searchable: true,
          },
        },
        {
          name: "inactiveAt",
          component: DatePicker,
          type: "datePicker",
          parent: "basicInfo-g1-top",
          props: {
            label: "Inactive Date",
            readOnly: true,
            placeholder: "",
          },
        },
        {
          name: "disposalStatus",
          component: Select,
          parent: "basicInfo-g1-top",
          props: {
            label: "Disposal Status",
            data: DATA_CLIENT_DISPOSAL_STATUS,
            readOnly: true,
          },
        },
        {
          name: "birthYear",
          component: NumberInput,
          parent: "basicInfo-g1-top",
          props: {
            label: "Year of Birth",
            readOnly: true,
          },
        },
        {
          name: "status",
          component: Select,
          parent: "section-basicInfo-corporate",
          props: {
            label: "Status",
            data: DATA_CLIENT_STATUS,
            required: true,
            readOnly: !canChangeClientStatus,
            searchable: true,
          },
        },
        {
          name: "inactiveAt",
          component: DatePicker,
          type: "datePicker",
          parent: "section-basicInfo-corporate",
          props: {
            label: "Inactive Date",
            readOnly: true,
            placeholder: "",
          },
        },
        {
          name: "disposalStatus",
          component: Select,
          parent: "section-basicInfo-corporate",
          props: {
            label: "Disposal Status",
            data: DATA_CLIENT_DISPOSAL_STATUS,
            readOnly: true,
          },
        },
        {
          name: "birthYear",
          component: NumberInput,
          parent: "section-basicInfo-corporate",
          props: {
            label: "Year of Birth",
            readOnly: true,
          },
        },
        {
          name: "salutation",
          component: Select,
          parent: "basicInfo-g1-top",
          props: {
            required: true,
            label: "Saluation",
            placeholder: "",
            data: DATA_SALUATATION,
            defaultValue: "Mr.",
            searchable: true,
          },
        },
        {
          name: "surname",
          component: TextInput,
          parent: "name-fields-surname",
          props: {
            required: true,
            placeholder: "Chan / Au Yeung",
            label: "Surname",
          },
        },
        {
          name: "givenName",
          component: TextInput,
          parent: "name-fields-given",
          props: {
            required: true,
            placeholder: "Tai Man / Man Tat",
            label: "Given Name",
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            required: true,
            placeholder: "Company ABC Ltd",
            label: "English Name (Company Name)",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "basicInfo-g1-bottom",
          props: {
            placeholder: "陳大文",
            label: "Chinese Name",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            placeholder: "大大有限公司",
            label: "Chinese Name (Company Name)",
          },
        },
        {
          name: "birthday",
          component: DatePicker,
          type: "datePicker",
          parent: "basicInfo-g1-bottom",
          props: {
            label: "Date of Birth",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "phones",
          component: PhoneNumberList,
          parent: "tab-wizard-phones",
          props: {},
        },
        {
          name: "emails",
          component: EmailList,
          parent: "tab-wizard-emails",
          props: {},
        },
        {
          name: "addresses",
          component: AddressList,
          parent: "tab-wizard-addresses",
          props: {},
        },
        {
          name: "contacts",
          component: ContactList,
          parent: "tab-wizard-related-persons",
          props: {
            phoneField: "tel",
          },
        },
        {
          name: "contactPerson",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            placeholder: "Kenny Chan",
            label: "Contact Person",
          },
        },
        {
          name: "parentCompany",
          component: RemoteSelect2,
          parent: "section-basicInfo-corporate",
          props: {
            label: "Parent Company",
            apiEntity: "client",
            required: true,
            placeholder: "Search Client By Name /BR ",
            valueField: "_id",
            labelField: "name",
            allowClear: true,
            labelRender: (data) =>
              `[ ${data.code ?? data.code ?? " -- "} ] ${data.name} ${
                data.cname ? `(${data.cname})` : ""
              } `,
            multiple: false,
            preQuery: { type: "CORPORATE" },
            searchFields: ["name", "id", "cname", "code"],
            clearable: true,
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },

        {
          name: "gender",
          component: Select,
          parent: "basicInfo-g1-bottom",
          props: {
            required: true,
            label: "Gender",
            placeholder: "",
            data: DATA_GENDERS,
            defaultValue: "Male",
            searchable: true,
          },
        },

        {
          name: "occupation",
          component: TextInput,
          parent: "basicInfo-g1-bottom",
          props: {
            required: true,
            placeholder: "Teacher",
            label: "Occupation",
          },
        },
        {
          name: "yearlySalary",
          component: NumberInput,
          parent: "basicInfo-g1-bottom",
          props: {
            label: "Year Salary",
            placeholder: "0",
            min: 0,
            hideControls: true,
          },
        },
        {
          name: "educationLevel",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Education Level",
            placeholder: "",
            data: DATA_EDUCATION_LEVEL,
            searchable: true,
            clearable: true,
          },
        },
        {
          name: "language",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Language",
            placeholder: "",
            data: DATA_LANGUAGE,
            defaultValue: "CANTONESE",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "maritalStatus",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Marital Status",
            placeholder: "",
            data: DATA_MARIAGE_STATUS,
            defaultValue: "Single",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "nationality",
          component: Select,

          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Nationality",
            placeholder: "",
            data: DATA_NATIONALITY,
            defaultValue: "Chinese",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "residentialCountry",
          component: Select,

          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Residential Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "placeOfBirth",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Country of Birth",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },
        {
          name: "accountCloseDate",
          component: DatePicker,
          type: "datePicker",
          parent: "basicInfo-g2",
          props: {
            label: "Account Close Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
            clearable: true,
          },
        },
        {
          name: "accountCloseDate",
          component: DatePicker,
          type: "datePicker",
          parent: "section-basicInfo-corporate",
          props: {
            label: "Account Close Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
            clearable: true,
          },
        },

        {
          name: "knowledgeOfDerivatives",
          component: Checkbox,
          parent: "section-pi",
          props: {
            label: "Investor with Knowledge of Derivatives",
            type: "checkbox",
            mb: 20,
          },
        },
        {
          name: "professionalInvestor",
          component: Checkbox,
          parent: "section-pi",
          props: {
            label: "Professional Investor",
            type: "checkbox",
            mb: 20,
          },
        },
        {
          name: "piEffectiveDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Effective Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "piAnnualConfirmationDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Annual Confirmation Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "piExpirationReminderDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Expiration Reminder Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "piTerminationDate",
          component: ClientPiDatePicker,
          type: "datePicker",
          parent: "section-pi",
          props: {
            label: "PI Termination Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "consultants",
          component: RemoteSelect2,
          parent: "section-consultant",
          props: {
            apiEntity: "user",
            required: true,
            // label: "Consultant",
            placeholder: "Assign consultant(s)",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) =>
              `${data.name ?? ""}${data.aliasName ? `,${data.aliasName}` : ""}${
                data.cname ? ` ${data.cname}` : ""
              } (${data.staffId ?? " -- "})`.trim(),
            multiple: true,
            // preQuery,
            // preQuery:  {isConsultant : true} ,
            searchFields: ["name", "staffId", "cname", "email", "aliasName"],
            sort: {
              by: "name",
              order: "desc",
            },
          },
        },

        {
          name: "consultantList",
          component: ConsultantList,
          parent: "section-consultant",
          props: {
            field: "consultants",
            mt: 0,
          },
        },

        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {
            validateOnMount: true,
            fieldLabels: CLIENT_CONFIRM_FIELD_LABELS,
            successText: clientLabel
              ? [
                  `You are updating existing client ${clientLabel}.`,
                  "Please click Confirm to save changes.",
                ]
              : [
                  "You are updating an existing client record.",
                  "Please click Confirm to save changes.",
                ],
          },
        },
      ],
    },
  };
  return schema;
};
